import React, {ReactElement, useCallback, useState} from 'react';

import Row from '../../../common/Row';
import {Point} from '../../../query/graphql';
import Button from '../buttons/Button';
import GenericCard from '../GenericCard';
import LocationInput from './LocationInput';
import ProximityDropdown from './ProximityDropdown';

type Props = {
  postcode: string | undefined;
  location: Point | undefined;
  proximity: number | undefined;
  onChange: (postcode?: string, location?: Point, proximity?: number) => void;
};

const SearchByLocationBox = ({postcode, location, proximity, onChange}: Props): ReactElement => {
  const [inputResetToggle, setInputResetToggle] = useState<boolean>(false);

  const handleChangeLocation = useCallback(
    async (newPostcode?: string, newLocation?: Point) => {
      onChange(newPostcode, newLocation, proximity);
    },
    [onChange, proximity]
  );

  const handleChangeProximity = useCallback(
    (value: number) => {
      onChange(postcode, location, value < 0 ? undefined : value);
    },
    [location, onChange, postcode]
  );

  const handleClear = useCallback(() => {
    if (postcode !== undefined || location !== undefined || proximity !== undefined) {
      onChange(undefined, undefined, undefined);
    }
    setInputResetToggle(prev => !prev); // Force reset of input
  }, [location, onChange, postcode, proximity]);

  return (
    <GenericCard border={'light'} className={'p-sm flex flex-col gap-xs !overflow-visible'}>
      <Row className={'items-center justify-between'}>
        Location
        <Button variant={'link'} onClick={handleClear}>
          <span className={'text-xs'}>Clear All</span>
        </Button>
      </Row>
      <LocationInput
        key={String(inputResetToggle)}
        postcode={postcode}
        location={location}
        onChange={handleChangeLocation}
      />
      <ProximityDropdown
        proximity={proximity ?? -1}
        disabled={!postcode && !location}
        onChange={handleChangeProximity}
      />
    </GenericCard>
  );
};

export default SearchByLocationBox;
